import React, { useMemo, useState } from 'react';
import {
  OdontogrammeState,
  ToothState,
  ToothFace,
  ToothWholeState,
  ToothSurfaceState,
} from '../../types';

/**
 * Odontogramme interactif (notation FDI).
 *
 * Porté depuis le projet Claude Design « Dentagramme interactif pour cabinet ».
 * Fonctionnalités :
 *  - Dentition adulte / lait
 *  - Disposition arcade (anatomique) / grille
 *  - Palette d'actes : carie, restauration, couronne, endodontie, coiffage, implant,
 *    bridge, extraction, dent absente, gomme
 *  - Annotation par face de la dent (M / D / O / V / L) via l'inspecteur
 *  - Bridges (connecteurs entre piliers)
 *  - Inspecteur de dent + récapitulatif des annotations
 */

type Tool =
  | 'select'
  | 'carie'
  | 'plombage'
  | 'couronne'
  | 'endo'
  | 'coiffage'
  | 'implant'
  | 'bridge'
  | 'extraire'
  | 'absente'
  | 'gomme';

interface DiagrammeDentaireProps {
  value: OdontogrammeState;
  onChange: (state: OdontogrammeState) => void;
  patientAge?: number;
  readOnly?: boolean;
}

const FACES: ToothFace[] = ['M', 'D', 'O', 'V', 'L'];

export const createEmptyOdontogramme = (
  patientAge?: number
): OdontogrammeState => ({
  dentition: patientAge !== undefined && patientAge < 12 ? 'lait' : 'adulte',
  teeth: {},
  bridges: [],
});

const emptyTooth = (): ToothState => ({
  whole: 'sain',
  surfaces: { M: null, D: null, O: null, V: null, L: null },
});

/** Liste plate des dents annotées — pour le champ `dents_concernees` (rétro-compat). */
export const deriveAffectedTeeth = (state: OdontogrammeState): string[] => {
  const set = new Set<string>();
  Object.entries(state.teeth).forEach(([fdi, ts]) => {
    if (ts.whole && ts.whole !== 'sain') set.add(fdi);
    FACES.forEach((k) => {
      if (ts.surfaces[k]) set.add(fdi);
    });
  });
  state.bridges.forEach((b) => b.teeth.forEach((fdi) => set.add(fdi)));
  return Array.from(set).sort((a, b) => +a - +b);
};

// ---- données statiques ----

const ordersFor = (dentition: 'adulte' | 'lait') => {
  if (dentition === 'lait') {
    return {
      upper: ['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'],
      lower: ['85', '84', '83', '82', '81', '71', '72', '73', '74', '75'],
    };
  }
  return {
    upper: ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'],
    lower: ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'],
  };
};

type ToothType = 'incisor' | 'canine' | 'premolar' | 'molar';

const toothMeta = (fdi: string, primary: boolean): { type: ToothType; name: string } => {
  const pos = +String(fdi)[1];
  if (!primary) {
    if (pos <= 2) return { type: 'incisor', name: pos === 1 ? 'Incisive centrale' : 'Incisive latérale' };
    if (pos === 3) return { type: 'canine', name: 'Canine' };
    if (pos <= 5) return { type: 'premolar', name: pos === 4 ? '1ère prémolaire' : '2e prémolaire' };
    return { type: 'molar', name: pos === 6 ? '1ère molaire' : pos === 7 ? '2e molaire' : '3e molaire (sagesse)' };
  }
  if (pos <= 2) return { type: 'incisor', name: pos === 1 ? 'Incisive centrale (lait)' : 'Incisive latérale (lait)' };
  if (pos === 3) return { type: 'canine', name: 'Canine (lait)' };
  return { type: 'molar', name: pos === 4 ? '1ère molaire (lait)' : '2e molaire (lait)' };
};

/**
 * Glyphe anatomique. Bord occlusal en y=0, couronne vers -cH (collet),
 * racines jusqu'à -(cH+rH). Émail blanc + racines beiges (vue vestibulaire).
 */
const buildGlyph = (type: ToothType, primary: boolean) => {
  let w: number, cH: number, rH: number;
  if (type === 'incisor') { w = 23; cH = 30; rH = 48; }
  else if (type === 'canine') { w = 24; cH = 34; rH = 58; }
  else if (type === 'premolar') { w = 24; cH = 26; rH = 46; }
  else { w = 34; cH = 26; rH = 42; }
  if (primary) { w *= 0.84; cH *= 0.86; rH *= 0.6; }
  const f = (n: number) => n.toFixed(1);
  let crown: string, root: string;
  if (type === 'incisor') {
    crown = `M ${f(-w * 0.4)},0 Q ${f(-w * 0.5)},${f(-cH * 0.4)} ${f(-w * 0.5)},${f(-cH * 0.78)} Q ${f(-w * 0.48)},${f(-cH)} ${f(-w * 0.3)},${f(-cH)} L ${f(w * 0.3)},${f(-cH)} Q ${f(w * 0.48)},${f(-cH)} ${f(w * 0.5)},${f(-cH * 0.78)} Q ${f(w * 0.5)},${f(-cH * 0.4)} ${f(w * 0.4)},0 Q 0,${f(cH * 0.14)} ${f(-w * 0.4)},0 Z`;
    root = `M ${f(-w * 0.4)},${f(-cH + 1)} C ${f(-w * 0.4)},${f(-cH - rH * 0.4)} ${f(-w * 0.18)},${f(-cH - rH * 0.85)} 0,${f(-cH - rH)} C ${f(w * 0.18)},${f(-cH - rH * 0.85)} ${f(w * 0.4)},${f(-cH - rH * 0.4)} ${f(w * 0.4)},${f(-cH + 1)} Z`;
  } else if (type === 'canine') {
    crown = `M ${f(-w * 0.42)},0 Q ${f(-w * 0.5)},${f(-cH * 0.5)} ${f(-w * 0.44)},${f(-cH * 0.8)} Q ${f(-w * 0.4)},${f(-cH)} ${f(-w * 0.24)},${f(-cH)} L ${f(w * 0.24)},${f(-cH)} Q ${f(w * 0.4)},${f(-cH)} ${f(w * 0.44)},${f(-cH * 0.8)} Q ${f(w * 0.5)},${f(-cH * 0.5)} ${f(w * 0.42)},0 L 0,${f(cH * 0.28)} Z`;
    root = `M ${f(-w * 0.38)},${f(-cH + 1)} C ${f(-w * 0.4)},${f(-cH - rH * 0.4)} ${f(-w * 0.16)},${f(-cH - rH * 0.88)} 0,${f(-cH - rH)} C ${f(w * 0.16)},${f(-cH - rH * 0.88)} ${f(w * 0.4)},${f(-cH - rH * 0.4)} ${f(w * 0.38)},${f(-cH + 1)} Z`;
  } else if (type === 'premolar') {
    crown = `M ${f(-w * 0.44)},0 Q ${f(-w * 0.5)},${f(-cH * 0.5)} ${f(-w * 0.45)},${f(-cH * 0.8)} Q ${f(-w * 0.4)},${f(-cH)} ${f(-w * 0.26)},${f(-cH)} L ${f(w * 0.26)},${f(-cH)} Q ${f(w * 0.4)},${f(-cH)} ${f(w * 0.45)},${f(-cH * 0.8)} Q ${f(w * 0.5)},${f(-cH * 0.5)} ${f(w * 0.44)},0 Q ${f(w * 0.24)},${f(cH * 0.2)} ${f(w * 0.12)},${f(cH * 0.04)} Q 0,${f(-cH * 0.12)} ${f(-w * 0.12)},${f(cH * 0.04)} Q ${f(-w * 0.24)},${f(cH * 0.2)} ${f(-w * 0.44)},0 Z`;
    root = `M ${f(-w * 0.36)},${f(-cH + 1)} C ${f(-w * 0.42)},${f(-cH - rH * 0.45)} ${f(-w * 0.2)},${f(-cH - rH * 0.85)} ${f(-w * 0.05)},${f(-cH - rH)} L ${f(w * 0.05)},${f(-cH - rH)} C ${f(w * 0.2)},${f(-cH - rH * 0.85)} ${f(w * 0.42)},${f(-cH - rH * 0.45)} ${f(w * 0.36)},${f(-cH + 1)} Z`;
  } else {
    crown = `M ${f(-w * 0.46)},0 Q ${f(-w * 0.5)},${f(-cH * 0.5)} ${f(-w * 0.46)},${f(-cH * 0.82)} Q ${f(-w * 0.42)},${f(-cH)} ${f(-w * 0.28)},${f(-cH)} L ${f(w * 0.28)},${f(-cH)} Q ${f(w * 0.42)},${f(-cH)} ${f(w * 0.46)},${f(-cH * 0.82)} Q ${f(w * 0.5)},${f(-cH * 0.5)} ${f(w * 0.46)},0 Q ${f(w * 0.36)},${f(cH * 0.22)} ${f(w * 0.24)},${f(cH * 0.05)} Q ${f(w * 0.12)},${f(-cH * 0.06)} 0,${f(cH * 0.06)} Q ${f(-w * 0.12)},${f(cH * 0.2)} ${f(-w * 0.24)},${f(cH * 0.05)} Q ${f(-w * 0.36)},${f(cH * 0.22)} ${f(-w * 0.46)},0 Z`;
    const rt = cH + rH;
    root = `M ${f(-w * 0.42)},${f(-cH + 1)} C ${f(-w * 0.5)},${f(-cH - rH * 0.45)} ${f(-w * 0.52)},${f(-rt * 0.92)} ${f(-w * 0.45)},${f(-rt)} C ${f(-w * 0.37)},${f(-rt * 0.9)} ${f(-w * 0.3)},${f(-cH - rH * 0.4)} ${f(-w * 0.14)},${f(-cH + 1)} Z `
      + `M ${f(w * 0.14)},${f(-cH + 1)} C ${f(w * 0.3)},${f(-cH - rH * 0.4)} ${f(w * 0.37)},${f(-rt * 0.9)} ${f(w * 0.45)},${f(-rt)} C ${f(w * 0.52)},${f(-rt * 0.92)} ${f(w * 0.5)},${f(-cH - rH * 0.45)} ${f(w * 0.42)},${f(-cH + 1)} Z`;
  }
  return { crown, root, cH, rH, w };
};

const geo = (
  i: number,
  n: number,
  arch: 'upper' | 'lower',
  disposition: 'arcade' | 'grille'
): { x: number; y: number; rot: number } => {
  if (disposition === 'grille') {
    const spacing = 46;
    const totalW = (n - 1) * spacing;
    const startX = 420 - totalW / 2;
    return { x: startX + i * spacing, y: arch === 'upper' ? 249 : 261, rot: arch === 'upper' ? 0 : 180 };
  }
  const Cx = 420;
  const Rx = n > 12 ? 332 : 252;
  const Ry = 16;
  const t = n === 1 ? 0.5 : i / (n - 1);
  const thDeg = 166 - t * 152;
  const th = (thDeg * Math.PI) / 180;
  const x = Cx + Rx * Math.cos(th);
  const s = Math.sin(th);
  const lean = (x - Cx) * 0.05;
  if (arch === 'upper') return { x, y: 234 + Ry * s, rot: lean };
  return { x, y: 246 + Ry * s, rot: 180 - lean };
};

const rot2 = (lx: number, ly: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [lx * c - ly * s, lx * s + ly * c];
};

const ACCENT = '#2b6cb0';
const SHOW_ROOTS = true;

const TOOLS: { id: Tool; label: string; color: string }[] = [
  { id: 'select', label: 'Sélection', color: '#94a3b8' },
  { id: 'carie', label: 'Carie', color: '#d64545' },
  { id: 'plombage', label: 'Restauration', color: '#3b7dd8' },
  { id: 'couronne', label: 'Couronne', color: '#c98a2b' },
  { id: 'endo', label: 'Endodontie', color: '#7c5bd9' },
  { id: 'coiffage', label: 'Coiffage', color: '#e0762f' },
  { id: 'implant', label: 'Implant', color: '#6b7787' },
  { id: 'bridge', label: 'Bridge', color: '#2faf9e' },
  { id: 'extraire', label: 'À extraire', color: '#d64545' },
  { id: 'absente', label: 'Absente', color: '#9aa5b1' },
  { id: 'gomme', label: 'Gomme', color: '#475569' },
];

const COLOR_OF: Record<string, string> = {
  carie: '#d64545', plombage: '#3b7dd8', couronne: '#c98a2b', implant: '#6b7787',
  absente: '#9aa5b1', extraire: '#d64545', endo: '#7c5bd9', coiffage: '#e0762f', bridge: '#2faf9e',
};
const LABEL_WHOLE: Record<string, string> = {
  couronne: 'Couronne', implant: 'Implant', absente: 'Absente', extraire: 'À extraire', endo: 'Endodontie',
  coiffage: 'Coiffage',
};

const LEGEND = [
  { label: 'Carie', color: '#d64545' }, { label: 'Restauration', color: '#3b7dd8' },
  { label: 'Couronne', color: '#c98a2b' }, { label: 'Endodontie', color: '#7c5bd9' },
  { label: 'Coiffage', color: '#e0762f' }, { label: 'Implant', color: '#6b7787' },
  { label: 'Bridge', color: '#2faf9e' }, { label: 'À extraire', color: '#d64545' },
  { label: 'Absente', color: '#9aa5b1' },
];

const DiagrammeDentaire: React.FC<DiagrammeDentaireProps> = ({
  value,
  onChange,
  patientAge,
  readOnly = false,
}) => {
  const [disposition, setDisposition] = useState<'arcade' | 'grille'>('arcade');
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [selected, setSelected] = useState<string | null>(null);
  const [pendingBridge, setPendingBridge] = useState<string | null>(null);

  const dentition = value.dentition;
  const primary = dentition === 'lait';
  const orders = useMemo(() => ordersFor(dentition), [dentition]);

  const getTS = (fdi: string): ToothState => value.teeth[fdi] || emptyTooth();

  // ---- mutations ----
  const patch = (partial: Partial<OdontogrammeState>) => onChange({ ...value, ...partial });

  const setTool = (id: Tool) => {
    setActiveTool(id);
    if (id !== 'bridge') setPendingBridge(null);
  };

  const setDentition = (id: 'adulte' | 'lait') => {
    setSelected(null);
    setPendingBridge(null);
    patch({ dentition: id });
  };

  const resetAll = () => {
    setSelected(null);
    setPendingBridge(null);
    patch({ teeth: {}, bridges: [] });
  };

  const toggleWhole = (fdi: string, st: ToothWholeState) => {
    const teeth = { ...value.teeth };
    const cur = teeth[fdi]
      ? { whole: teeth[fdi].whole, surfaces: { ...teeth[fdi].surfaces } }
      : emptyTooth();
    cur.whole = cur.whole === st ? 'sain' : st;
    teeth[fdi] = cur;
    patch({ teeth });
  };

  const applySurface = (key: ToothFace) => {
    const fdi = selected;
    if (!fdi) return;
    if (activeTool !== 'carie' && activeTool !== 'plombage' && activeTool !== 'gomme') return;
    const teeth = { ...value.teeth };
    const cur = teeth[fdi]
      ? { whole: teeth[fdi].whole, surfaces: { ...teeth[fdi].surfaces } }
      : emptyTooth();
    cur.surfaces[key] =
      activeTool === 'gomme'
        ? null
        : cur.surfaces[key] === activeTool
        ? null
        : (activeTool as ToothSurfaceState);
    teeth[fdi] = cur;
    patch({ teeth });
  };

  const clearTooth = (fdi: string) => {
    const teeth = { ...value.teeth };
    delete teeth[fdi];
    const bridges = value.bridges.filter((b) => !b.teeth.includes(fdi));
    patch({ teeth, bridges });
  };

  const handleBridge = (fdi: string) => {
    if (!pendingBridge) {
      setPendingBridge(fdi);
      setSelected(fdi);
      return;
    }
    if (pendingBridge === fdi) {
      setPendingBridge(null);
      return;
    }
    let arr: string[] | null = null;
    for (const a of [orders.upper, orders.lower]) {
      if (a.includes(pendingBridge) && a.includes(fdi)) { arr = a; break; }
    }
    if (!arr) {
      setPendingBridge(fdi);
      setSelected(fdi);
      return;
    }
    let i1 = arr.indexOf(pendingBridge);
    let i2 = arr.indexOf(fdi);
    if (i1 > i2) { const t = i1; i1 = i2; i2 = t; }
    const span = arr.slice(i1, i2 + 1);
    patch({ bridges: value.bridges.concat([{ teeth: span }]) });
    setPendingBridge(null);
    setSelected(fdi);
  };

  const clickTooth = (fdi: string) => {
    if (readOnly) return;
    if (activeTool === 'bridge') { handleBridge(fdi); return; }
    if (activeTool === 'gomme') { clearTooth(fdi); setSelected(fdi); return; }
    if (activeTool === 'select' || activeTool === 'carie' || activeTool === 'plombage') {
      setSelected(fdi);
      return;
    }
    toggleWhole(fdi, activeTool as ToothWholeState);
    setSelected(fdi);
  };

  // ---- calcul du rendu ----
  const chart = useMemo(() => {
    const teeth: any[] = [];
    const marks: any[] = [];
    const numbers: any[] = [];
    const pos: Record<string, { x: number; y: number; rot: number }> = {};
    const arches: [('upper' | 'lower'), string[]][] = [['upper', orders.upper], ['lower', orders.lower]];

    for (const [arch, list] of arches) {
      const n = list.length;
      for (let i = 0; i < n; i++) {
        const fdi = list[i];
        const g = geo(i, n, arch, disposition);
        pos[fdi] = g;
        const { type } = toothMeta(fdi, primary);
        const gl = buildGlyph(type, primary);
        const w = gl.w, cH = gl.cH, rH = gl.rH;
        const ts = getTS(fdi);
        const whole = ts.whole;
        const isCrown = whole === 'couronne';
        const isImplant = whole === 'implant';
        const isAbsent = whole === 'absente';
        const isExtract = whole === 'extraire';
        const isEndo = whole === 'endo';
        const isCoiffage = whole === 'coiffage';
        const inBridge = value.bridges.some((b) => b.teeth.includes(fdi));
        const isPontic = inBridge && isAbsent;
        const isSelected = selected === fdi;
        const isPending = pendingBridge === fdi;

        let crownFill = '#ffffff', crownStroke = '#3a4654', crownStrokeW = 1.4;
        let rootFill = '#eaca97';
        if (isCrown) { crownFill = '#f2ce80'; }
        else if (isImplant) { crownFill = '#dde3e9'; crownStroke = '#5a6675'; }
        else if (isAbsent) { crownFill = '#eef1f4'; crownStroke = '#bcc6d0'; rootFill = '#e7e4db'; }
        else if (isCoiffage) { crownFill = '#fbe8d8'; }
        if (isEndo) { rootFill = '#c3b1ec'; }
        if (isExtract) { crownStroke = '#d64545'; crownStrokeW = 1.8; }
        if (isPending) { crownStroke = '#2faf9e'; crownStrokeW = 2.4; }
        if (isSelected) { crownStroke = ACCENT; crownStrokeW = 2.4; }

        teeth.push({
          key: fdi, fdi,
          transform: `translate(${g.x.toFixed(1)},${g.y.toFixed(1)}) rotate(${g.rot.toFixed(1)})`,
          opacity: isAbsent && !isPontic ? 0.4 : 1,
          crownPath: gl.crown,
          crownFill, crownStroke, crownStrokeW,
          rootsPath: gl.root,
          rootsFill: rootFill,
          rootStroke: isAbsent ? '#c2ccd6' : '#3a4654',
          showRoots: SHOW_ROOTS && !isImplant,
          showImplant: isImplant,
          implantPath: `M -6,${(-cH - 3).toFixed(1)} H 6 M -5,${(-cH - 9).toFixed(1)} H 5 M -4,${(-cH - 15).toFixed(1)} H 4 M 0,${(-cH - 1).toFixed(1)} V ${(-cH - 19).toFixed(1)}`,
          showRing: isCrown,
          // Coiffage pulpaire : liseré en forme de coiffe côté occlusal
          showCoiffage: isCoiffage,
          coiffagePath: `M ${(-w * 0.32).toFixed(1)},${(-cH * 0.3).toFixed(1)} Q 0,${(-cH * 0.62).toFixed(1)} ${(w * 0.32).toFixed(1)},${(-cH * 0.3).toFixed(1)}`,
          isPontic,
          showX: isAbsent || isExtract,
          xColor: isExtract ? '#d64545' : '#9aa5b1',
          xPath: `M ${(-w * 0.3).toFixed(1)},${(-cH * 0.82).toFixed(1)} L ${(w * 0.3).toFixed(1)},${(-cH * 0.08).toFixed(1)} M ${(w * 0.3).toFixed(1)},${(-cH * 0.82).toFixed(1)} L ${(-w * 0.3).toFixed(1)},${(-cH * 0.08).toFixed(1)}`,
          isSelected,
        });

        const off: Record<ToothFace, [number, number]> = {
          O: [0, -cH * 0.16], V: [0, -cH * 0.5], M: [-w * 0.28, -cH * 0.52],
          D: [w * 0.28, -cH * 0.52], L: [0, -cH * 0.82],
        };
        for (const k of FACES) {
          const s = ts.surfaces[k];
          if (s) {
            const [rx, ry] = rot2(off[k][0], off[k][1], g.rot);
            marks.push({ key: `${fdi}-${k}`, cx: (g.x + rx).toFixed(1), cy: (g.y + ry).toFixed(1), fill: s === 'carie' ? '#d64545' : '#3b7dd8' });
          }
        }

        const [nrx, nry] = rot2(0, -(cH + rH + 11), g.rot);
        numbers.push({
          key: fdi, fdi, x: (g.x + nrx).toFixed(1), y: (g.y + nry).toFixed(1),
          color: isExtract ? '#d64545' : isAbsent ? '#9aa5b1' : isSelected ? ACCENT : '#46566a',
          weight: isSelected ? 700 : 600,
        });
      }
    }

    const connectors = value.bridges
      .map((b, idx) => {
        const pts = b.teeth
          .map((fdi) => {
            const g = pos[fdi];
            if (!g) return null;
            const [bx, by] = rot2(0, -13, g.rot);
            return `${(g.x + bx).toFixed(1)},${(g.y + by).toFixed(1)}`;
          })
          .filter(Boolean)
          .join(' ');
        return { key: idx, points: pts };
      })
      .filter((c) => c.points);

    const grid = disposition === 'grille';
    const xsAll = Object.values(pos).map((p) => p.x);
    const minX = xsAll.length ? Math.min(...xsAll) : 120;
    const maxX = xsAll.length ? Math.max(...xsAll) : 720;
    const band = grid
      ? { x: minX - 30, y: 243, w: maxX - minX + 60, h: 24, r: 12 }
      : { x: minX - 26, y: 239, w: maxX - minX + 52, h: 26, r: 13 };
    const viewBox = grid
      ? (() => {
          const n = primary ? 10 : 16;
          const totalW = (n - 1) * 46;
          const startX = 420 - totalW / 2;
          return `${(startX - 50).toFixed(0)} 118 ${(totalW + 100).toFixed(0)} 276`;
        })()
      : '88 116 664 280';

    return {
      teeth, marks, numbers, connectors, band, viewBox,
      midTop: grid ? 150 : 148, midBot: grid ? 372 : 366,
      labTopY: grid ? 144 : 142, labBotY: grid ? 388 : 384,
    };
  }, [value, disposition, selected, pendingBridge, primary, orders]);

  // ---- inspecteur ----
  const at = TOOLS.find((t) => t.id === activeTool)!;
  const hints: Record<Tool, string> = {
    select: 'cliquez une dent pour la sélectionner',
    carie: 'sélectionnez une dent puis cliquez une face',
    plombage: 'sélectionnez une dent puis cliquez une face',
    couronne: 'cliquez une dent pour (dé)poser',
    endo: 'cliquez une dent pour marquer',
    coiffage: 'cliquez une dent pour marquer un coiffage',
    implant: 'cliquez une dent',
    bridge: pendingBridge ? `pilier ${pendingBridge} — cliquez la 2e dent` : 'cliquez les 2 dents piliers',
    extraire: 'cliquez une dent à extraire',
    absente: 'cliquez une dent absente',
    gomme: 'cliquez une dent ou une face',
  };

  const inspector = useMemo(() => {
    if (!selected) return null;
    const { type, name } = toothMeta(selected, primary);
    const q = +String(selected)[0];
    const side: 'right' | 'left' = [1, 4, 5, 8].indexOf(q) >= 0 ? 'right' : 'left';
    const ts = getTS(selected);
    const leftKey: ToothFace = side === 'right' ? 'D' : 'M';
    const rightKey: ToothFace = side === 'right' ? 'M' : 'D';
    const occ = type === 'incisor' || type === 'canine' ? 'I' : 'O';
    const fillOf = (s: ToothSurfaceState | null) => (s === 'carie' ? '#d64545' : s === 'plombage' ? '#3b7dd8' : '#ffffff');
    const txtOf = (s: ToothSurfaceState | null) => (s ? '#ffffff' : '#7b8a99');
    const zdef: { key: ToothFace; label: string; pts: string; cx: number; cy: number }[] = [
      { key: 'V', label: 'V', pts: '0,0 160,0 112,48 48,48', cx: 80, cy: 22 },
      { key: rightKey, label: rightKey, pts: '160,0 160,160 112,112 112,48', cx: 136, cy: 80 },
      { key: 'L', label: 'L', pts: '0,160 160,160 112,112 48,112', cx: 80, cy: 140 },
      { key: leftKey, label: leftKey, pts: '0,0 0,160 48,112 48,48', cx: 24, cy: 80 },
      { key: 'O', label: occ, pts: '48,48 112,48 112,112 48,112', cx: 80, cy: 82 },
    ];
    const zones = zdef.map((z) => ({
      key: z.key, label: z.label, pts: z.pts, cx: z.cx, cy: z.cy,
      fill: fillOf(ts.surfaces[z.key]), txt: txtOf(ts.surfaces[z.key]),
    }));
    const surfTool = activeTool === 'carie' || activeTool === 'plombage';
    const chipDefs: { id: ToothWholeState; label: string; color: string }[] = [
      { id: 'couronne', label: 'Couronne', color: '#c98a2b' },
      { id: 'endo', label: 'Endodontie', color: '#7c5bd9' },
      { id: 'coiffage', label: 'Coiffage', color: '#e0762f' },
      { id: 'implant', label: 'Implant', color: '#6b7787' },
      { id: 'extraire', label: 'À extraire', color: '#d64545' },
      { id: 'absente', label: 'Absente', color: '#9aa5b1' },
    ];
    return {
      fdi: selected, name, quadrant: 'Quadrant ' + q, side: side === 'right' ? 'Côté droit' : 'Côté gauche',
      zones, chipDefs, whole: ts.whole,
      faceHint: surfTool
        ? `Cliquez une face pour appliquer : ${at.label}.`
        : 'Sélectionnez « Carie » ou « Restauration » dans la palette pour annoter une face.',
    };
  }, [selected, primary, activeTool, value, at.label]);

  // ---- récapitulatif ----
  const summary = useMemo(() => {
    const out: { key: string; fdi: string; text: string; color: string; sort: number }[] = [];
    for (const fdi of Object.keys(value.teeth)) {
      const ts = value.teeth[fdi];
      if (ts.whole && ts.whole !== 'sain') {
        out.push({ key: `${fdi}-w`, fdi, text: LABEL_WHOLE[ts.whole], color: COLOR_OF[ts.whole], sort: +fdi });
      }
      const car: string[] = [], plo: string[] = [];
      for (const k of FACES) {
        const s = ts.surfaces[k];
        if (s === 'carie') car.push(k);
        if (s === 'plombage') plo.push(k);
      }
      if (car.length) out.push({ key: `${fdi}-c`, fdi, text: 'Carie (' + car.join(', ') + ')', color: COLOR_OF.carie, sort: +fdi });
      if (plo.length) out.push({ key: `${fdi}-p`, fdi, text: 'Restauration (' + plo.join(', ') + ')', color: COLOR_OF.plombage, sort: +fdi });
    }
    value.bridges.forEach((b, idx) => {
      out.push({
        key: `b-${idx}`, fdi: b.teeth[0] + '–' + b.teeth[b.teeth.length - 1],
        text: 'Bridge (' + b.teeth.length + ' dents)', color: COLOR_OF.bridge, sort: +b.teeth[0],
      });
    });
    out.sort((a, b) => a.sort - b.sort);
    return out;
  }, [value]);

  // ---- styles partagés ----
  const segBtn = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', background: active ? '#fff' : 'transparent', color: active ? ACCENT : '#94a3b8',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
  });

  return (
    <div style={{ background: '#eef1f5', border: '1px solid #e6ebf1', borderRadius: 14, overflow: 'hidden', color: '#1f2a37' }}>
      <style>{`@keyframes dgsel{0%,100%{stroke-opacity:1}50%{stroke-opacity:.35}}`}</style>

      {/* Barre d'outils supérieure */}
      <header style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e3e8ee' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>D</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Dentagramme</span>
            <span style={{ fontSize: 10.5, color: '#94a3b8' }}>Odontogramme FDI{patientAge !== undefined ? ` · ${patientAge} ans` : ''}</span>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Dentition</span>
          <div style={{ display: 'flex', background: '#eef1f5', borderRadius: 8, padding: 3, gap: 2 }}>
            {(['adulte', 'lait'] as const).map((id) => (
              <button key={id} type="button" disabled={readOnly} onClick={() => setDentition(id)} style={segBtn(dentition === id)}>
                {id === 'adulte' ? 'Adulte' : 'Lait'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Disposition</span>
          <div style={{ display: 'flex', background: '#eef1f5', borderRadius: 8, padding: 3, gap: 2 }}>
            {(['arcade', 'grille'] as const).map((id) => (
              <button key={id} type="button" onClick={() => setDisposition(id)} style={segBtn(disposition === id)}>
                {id === 'arcade' ? 'Arcade' : 'Grille'}
              </button>
            ))}
          </div>
        </div>

        {!readOnly && (
          <button type="button" onClick={resetAll} style={{ padding: '7px 13px', background: '#fff', border: '1px solid #e0e6ed', borderRadius: 8, fontSize: 12.5, color: '#64748b', cursor: 'pointer', fontWeight: 500 }}>
            Réinitialiser
          </button>
        )}
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 0 }}>
        {/* Zone principale */}
        <main style={{ flex: '1 1 520px', display: 'flex', flexDirection: 'column', padding: '16px 18px', gap: 14, minWidth: 0 }}>
          {!readOnly && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
              {TOOLS.map((t) => {
                const active = t.id === activeTool;
                return (
                  <button
                    key={t.id} type="button" onClick={() => setTool(t.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 9,
                      fontSize: 12.5, fontWeight: 500, cursor: 'pointer', transition: 'all .12s',
                      border: `1px solid ${active ? t.color : '#e3e8ee'}`, background: active ? t.color : '#fff',
                      color: active ? '#fff' : '#475569', boxShadow: active ? `0 1px 4px ${t.color}55` : 'none',
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: active ? '#fff' : t.color }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e6ebf1', borderRadius: 14, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 18px', borderBottom: '1px solid #f0f3f7' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#64748b' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: at.color }} />
                <span>Outil actif&nbsp;: <strong style={{ color: '#334155', fontWeight: 600 }}>{at.label}</strong></span>
                <span style={{ color: '#cbd5e1' }}>·</span>
                <span>{hints[activeTool]}</span>
              </div>
              <span style={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'ui-monospace,monospace' }}>
                {summary.length} annotation{summary.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '8px 14px' }}>
              <svg viewBox={chart.viewBox} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', minHeight: 320, display: 'block' }}>
                <line x1={420} y1={chart.midTop} x2={420} y2={chart.midBot} stroke="#dce3eb" strokeWidth={1.2} strokeDasharray="3 6" />
                <text x={426} y={chart.labTopY} fontSize={11} fill="#aab6c4" fontFamily="ui-monospace,monospace">Maxillaire</text>
                <text x={426} y={chart.labBotY} fontSize={11} fill="#aab6c4" fontFamily="ui-monospace,monospace">Mandibule</text>

                <rect x={chart.band.x} y={chart.band.y} width={chart.band.w} height={chart.band.h} rx={chart.band.r} fill="#d8786c" />

                {chart.connectors.map((cn) => (
                  <polyline key={cn.key} points={cn.points} fill="none" stroke="#2faf9e" strokeWidth={7} strokeLinecap="round" strokeLinejoin="round" opacity={0.4} />
                ))}

                {chart.teeth.map((tt) => (
                  <g key={tt.key} transform={tt.transform} opacity={tt.opacity} onClick={() => clickTooth(tt.fdi)} style={{ cursor: readOnly ? 'default' : 'pointer' }}>
                    {tt.showRoots && (
                      <path d={tt.rootsPath} fill={tt.rootsFill} stroke={tt.rootStroke} strokeWidth={1.3} strokeLinejoin="round" />
                    )}
                    {tt.showImplant && (
                      <path d={tt.implantPath} fill="none" stroke="#6b7787" strokeWidth={2.2} strokeLinecap="round" />
                    )}
                    <path
                      d={tt.crownPath} fill={tt.crownFill} stroke={tt.crownStroke} strokeWidth={tt.crownStrokeW} strokeLinejoin="round"
                      style={tt.isSelected ? { animation: 'dgsel 1.3s ease-in-out infinite' } : undefined}
                    />
                    {tt.showRing && (
                      <path d={tt.crownPath} fill="none" stroke="#c98a2b" strokeWidth={1} opacity={0.55} />
                    )}
                    {tt.showCoiffage && (
                      <path d={tt.coiffagePath} fill="none" stroke="#e0762f" strokeWidth={2.4} strokeLinecap="round" />
                    )}
                    {tt.isPontic && (
                      <path d={tt.crownPath} fill="none" stroke="#2faf9e" strokeWidth={1.6} strokeDasharray="3 3" />
                    )}
                    {tt.showX && (
                      <path d={tt.xPath} stroke={tt.xColor} strokeWidth={2.3} strokeLinecap="round" />
                    )}
                  </g>
                ))}

                {chart.marks.map((mk) => (
                  <circle key={mk.key} cx={mk.cx} cy={mk.cy} r={3.1} fill={mk.fill} stroke="#fff" strokeWidth={0.8} />
                ))}

                {chart.numbers.map((nb) => (
                  <text key={nb.key} x={nb.x} y={nb.y} fill={nb.color} fontSize={13.5} fontWeight={nb.weight} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" style={{ pointerEvents: 'none' }}>
                    {nb.fdi}
                  </text>
                ))}
              </svg>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '2px 4px' }}>
            {LEGEND.map((lg) => (
              <div key={lg.label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: lg.color }} />
                <span style={{ fontSize: 11.5, color: '#64748b' }}>{lg.label}</span>
              </div>
            ))}
          </div>
        </main>

        {/* Inspecteur / récapitulatif */}
        <aside style={{ width: 336, flex: 'none', maxWidth: '100%', background: '#fff', borderLeft: '1px solid #e3e8ee', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {inspector ? (
            <>
              <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f3f7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 11, background: '#eef4fb', border: '1px solid #dbe7f4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'ui-monospace,monospace', fontSize: 18, fontWeight: 600, color: ACCENT }}>
                    {inspector.fdi}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f2a37' }}>{inspector.name}</span>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{inspector.quadrant} · {inspector.side}</span>
                  </div>
                </div>
              </div>

              <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f3f7', display: 'flex', flexDirection: 'column', gap: 13 }}>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94a3b8' }}>Faces de la dent</span>
                <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
                  <svg viewBox="0 0 160 160" style={{ width: 150, height: 150, flex: 'none' }}>
                    {inspector.zones.map((z) => (
                      <polygon key={z.key} points={z.pts} fill={z.fill} stroke="#d6dee7" strokeWidth={1.4} onClick={() => applySurface(z.key)} style={{ cursor: readOnly ? 'default' : 'pointer' }} />
                    ))}
                    {inspector.zones.map((z) => (
                      <text key={z.key + '-t'} x={z.cx} y={z.cy} fontSize={11} fontWeight={600} fill={z.txt} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,monospace" style={{ pointerEvents: 'none' }}>
                        {z.label}
                      </text>
                    ))}
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11.5, color: '#7b8a99', lineHeight: 1.5 }}>
                    <span><strong style={{ color: '#5b6b7b' }}>V</strong> Vestibulaire</span>
                    <span><strong style={{ color: '#5b6b7b' }}>L</strong> Lingual / Palatin</span>
                    <span><strong style={{ color: '#5b6b7b' }}>M</strong> Mésial</span>
                    <span><strong style={{ color: '#5b6b7b' }}>D</strong> Distal</span>
                    <span><strong style={{ color: '#5b6b7b' }}>O</strong> Occlusal / Incisal</span>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>{inspector.faceHint}</p>
              </div>

              {!readOnly && (
                <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f3f7', display: 'flex', flexDirection: 'column', gap: 11 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94a3b8' }}>État global de la dent</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {inspector.chipDefs.map((c) => {
                      const active = inspector.whole === c.id;
                      return (
                        <button
                          key={c.id} type="button" onClick={() => toggleWhole(inspector.fdi, c.id)}
                          style={{
                            padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                            border: `1px solid ${active ? c.color : '#e3e8ee'}`, background: active ? c.color : '#fff', color: active ? '#fff' : '#475569',
                          }}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button" onClick={() => clearTooth(inspector.fdi)}
                    style={{ marginTop: 3, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#fff', border: '1px solid #e9d6d6', borderRadius: 8, fontSize: 12, color: '#c25151', cursor: 'pointer', fontWeight: 500 }}
                  >
                    Effacer la dent {inspector.fdi}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '26px 22px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', borderBottom: '1px solid #f0f3f7' }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f1f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aab6c4', fontSize: 18 }}>◎</div>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Aucune dent sélectionnée</span>
              <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                {readOnly
                  ? 'Cliquez une dent pour consulter ses faces.'
                  : 'Choisissez un acte dans la palette, puis cliquez une dent. Sélectionnez une dent pour annoter ses faces.'}
              </span>
            </div>
          )}

          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '15px 20px 9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94a3b8' }}>Récapitulatif</span>
              <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace,monospace' }}>{summary.length}</span>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0 14px 14px', maxHeight: 260 }}>
              {summary.length === 0 ? (
                <div style={{ padding: 10, fontSize: 12, color: '#aab6c4' }}>Aucune annotation pour l'instant.</div>
              ) : (
                summary.map((sm) => (
                  <div key={sm.key} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 8px', borderRadius: 8 }}>
                    <span style={{ width: 34, flex: 'none', textAlign: 'center', fontFamily: 'ui-monospace,monospace', fontSize: 11, fontWeight: 600, color: '#5b6b7b', background: '#f3f6f9', borderRadius: 5, padding: '3px 0' }}>
                      {sm.fdi}
                    </span>
                    <span style={{ width: 9, height: 9, borderRadius: 2, flex: 'none', background: sm.color }} />
                    <span style={{ fontSize: 12.5, color: '#334155' }}>{sm.text}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default DiagrammeDentaire;
